import { Link, useLocation } from "react-router-dom";
import logo from "../assets/logo.png";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { useState } from "react";

/* ─── Palette matching site theme ─── */
const C = {
  saffron: "#E8922A", saffronL: "#FDF0D5", saffronD: "#B86A10",
  sage: "#4A7C59", sageL: "#EAF4EE", sageD: "#2D5A3D",
  terra: "#B85C38",
  cream: "#FDFAF3", creamD: "#F5EDD8",
  turmeric: "#F7C948",
  ink: "#2C1810", inkMid: "#5C3D2E", inkLight: "#9A7B60",
};

/* ─── Inline Lotus SVG ─── */
const Lotus = ({ size = 32, color = C.saffron }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none">
    {[0,30,60,-30,-60,90].map((r,i) => (
      <ellipse key={i} cx="40" cy="50" rx="7" ry="16"
        fill={color} opacity={i > 3 ? 0.45 : i > 1 ? 0.7 : 0.95}
        transform={`rotate(${r} 40 50)`}/>
    ))}
    <circle cx="40" cy="44" r="7" fill={C.turmeric}/>
    <rect x="38" y="58" width="4" height="10" rx="2" fill={C.sage} opacity="0.55"/>
  </svg>
);

const NAV_LINKS = [
  { path: "/",          label: "Home"       },
  { path: "/about",     label: "About"      },
  { path: "/services",  label: "Services"   },
  { path: "/our-doctors", label: "Our Doctors" },
  { path: "/request",   label: "Appointment", cta: true },
  { path: "/status",    label: "Status"     },
];

export default function Navbar() {
  const { pathname } = useLocation();
  const [open, setOpen]       = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const { scrollY } = useScroll();
  useMotionValueEvent(scrollY, "change", (v) => setScrolled(v > 40));

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;900&display=swap');

        .ha-brand {
          font-family: 'Cinzel', 'Playfair Display', serif !important;
          letter-spacing: 0.05em;
        }
        .ha-nav-link {
          font-family: 'Inter', sans-serif;
          font-size: 13.5px;
          font-weight: 500;
          letter-spacing: 0.03em;
          text-decoration: none;
          position: relative;
          transition: color 0.22s;
        }
        .ha-nav-link::after {
          content: '';
          position: absolute;
          bottom: -3px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 2px;
          background: linear-gradient(90deg, #E8922A, #B85C38);
          border-radius: 2px;
          transition: width 0.28s cubic-bezier(.4,0,.2,1);
        }
        .ha-nav-link:hover::after,
        .ha-nav-link.ha-active::after { width: 70%; }
        .ha-nav-link.ha-active { color: #B86A10 !important; }
        .ha-nav-link:hover { color: #B86A10 !important; }
        .ha-hamburger {
          background: none; border: none; cursor: pointer;
          padding: 8px; display: flex; flex-direction: column;
          gap: 5px; align-items: flex-end;
        }
        @media (min-width: 901px) { .ha-mobile-only { display: none !important; } }
        @media (max-width: 900px)  { .ha-desktop-only { display: none !important; } }
      `}</style>

      <motion.nav
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.55, ease: [0.4, 0, 0.2, 1] }}
        style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999,
          background: scrolled ? "rgba(253,250,243,0.97)" : "rgba(253,250,243,0.88)",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
          borderBottom: scrolled ? `1px solid ${C.saffron}28` : "1px solid transparent",
          boxShadow: scrolled ? "0 4px 32px rgba(44,24,16,0.09)" : "none",
          transition: "background 0.35s, box-shadow 0.35s, border-color 0.35s",
        }}
      >
        {/* ── Decorative gradient top bar ── */}
        <div style={{
          height: 3,
          background: `linear-gradient(90deg, ${C.sageD} 0%, ${C.sage} 20%, ${C.saffron} 42%, ${C.terra} 60%, ${C.saffron} 78%, ${C.sageD} 100%)`,
        }} />

        <div style={{
          maxWidth: 1200, margin: "0 auto", padding: "0 clamp(14px,4vw,28px)",
          height: 68, display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>

          {/* ── BRAND ── */}
          <Link to="/" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 10 }}>
            <motion.img
              src={logo}
              alt="Heramba Ayurveda"
              whileHover={{ scale: 1.06 }}
              transition={{ duration: 0.3 }}
              style={{ width: 52, height: 52, objectFit: "contain" }}
            />
            <div>
              <motion.span
                whileHover={{ scale: 1.02 }}
                className="ha-brand"
                style={{ display: "block", fontSize: 21, fontWeight: 700, color: C.sageD, lineHeight: 1.15 }}
              >
                Heramba
              </motion.span>
              <span
                className="ha-brand"
                style={{
                  display: "block", fontSize: 10, fontWeight: 600,
                  color: C.saffronD, letterSpacing: "0.25em",
                  textTransform: "uppercase", lineHeight: 1.1,
                }}
              >
                Ayurveda
              </span>
            </div>
          </Link>

          {/* ── DESKTOP LINKS ── */}
          <div className="ha-desktop-only" style={{ display: "flex", alignItems: "center", gap: 2 }}>
            {NAV_LINKS.map(({ path, label, cta }) => {
              const isActive = pathname === path;
              if (cta) {
                return (
                  <Link key={path} to={path} style={{ textDecoration: "none", marginLeft: 10 }}>
                    <motion.div
                      whileHover={{ scale: 1.06, y: -1, boxShadow: "0 12px 30px rgba(232,146,42,0.52)" }}
                      whileTap={{ scale: 0.95 }}
                      style={{
                        padding: "9px 24px", borderRadius: 50,
                        background: `linear-gradient(135deg, ${C.saffron}, ${C.terra})`,
                        color: "#fff", fontSize: 13, fontWeight: 700,
                        fontFamily: "'Inter', sans-serif", letterSpacing: "0.04em",
                        boxShadow: `0 6px 20px rgba(232,146,42,0.35)`,
                        cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
                      }}
                    >
                      🌿 {label}
                    </motion.div>
                  </Link>
                );
              }
              return (
                <Link
                  key={path} to={path}
                  className={`ha-nav-link${isActive ? " ha-active" : ""}`}
                  style={{ color: isActive ? C.saffronD : C.inkMid, padding: "10px 14px", display: "block" }}
                >
                  {label}
                </Link>
              );
            })}
          </div>

          {/* ── HAMBURGER ── */}
          <button className="ha-hamburger ha-mobile-only" onClick={() => setOpen(!open)} aria-label="Toggle menu">
            <motion.div animate={open ? { rotate: 45, y: 7 } : { rotate: 0, y: 0 }} transition={{ duration: 0.22 }}
              style={{ width: 24, height: 2, background: C.sageD, borderRadius: 2 }} />
            <motion.div animate={open ? { opacity: 0, scaleX: 0 } : { opacity: 1, scaleX: 1 }} transition={{ duration: 0.15 }}
              style={{ width: 18, height: 2, background: C.saffron, borderRadius: 2 }} />
            <motion.div animate={open ? { rotate: -45, y: -7 } : { rotate: 0, y: 0 }} transition={{ duration: 0.22 }}
              style={{ width: 24, height: 2, background: C.sageD, borderRadius: 2 }} />
          </button>
        </div>

        {/* ── MOBILE MENU ── */}
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.32, ease: [0.4, 0, 0.2, 1] }}
              style={{
                overflow: "hidden",
                background: "rgba(253,250,243,0.98)",
                backdropFilter: "blur(20px)",
                borderTop: `1px solid ${C.saffronL}`,
              }}
            >
              <div style={{ padding: "16px 24px 24px" }}>
                {/* Mobile brand header */}
                <div style={{
                  display: "flex", alignItems: "center", gap: 8,
                  marginBottom: 16, paddingBottom: 14,
                  borderBottom: `1px solid ${C.saffronL}`,
                }}>
                  <img src={logo} alt="Heramba Ayurveda" style={{ width: 30, height: 30, objectFit: "contain" }} />
                  <span className="ha-brand" style={{ fontSize: 14, fontWeight: 700, color: C.sageD }}>
                    Heramba Ayurveda
                  </span>
                </div>

                {NAV_LINKS.map(({ path, label, cta }, i) => {
                  const isActive = pathname === path;
                  return (
                    <motion.div key={path}
                      initial={{ opacity: 0, x: -16 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.055, duration: 0.26 }}
                    >
                      <Link to={path} onClick={() => setOpen(false)} style={{ textDecoration: "none" }}>
                        <motion.div whileHover={{ x: 6 }} whileTap={{ scale: 0.97 }}
                          style={{
                            display: "flex", alignItems: "center", gap: 10,
                            padding: "11px 14px", borderRadius: 12, marginBottom: 4,
                            background: cta
                              ? `linear-gradient(135deg, ${C.saffron}, ${C.terra})`
                              : isActive ? C.saffronL : "transparent",
                            border: isActive && !cta ? `1px solid ${C.saffron}35` : "1px solid transparent",
                          }}
                        >
                          {isActive && !cta && (
                            <div style={{ width: 5, height: 5, borderRadius: "50%", background: C.saffron, flexShrink: 0 }} />
                          )}
                          {cta && <span style={{ fontSize: 12 }}>🌿</span>}
                          <span style={{
                            fontSize: 14, fontWeight: cta ? 700 : 500,
                            color: cta ? "#fff" : isActive ? C.saffronD : C.inkMid,
                            fontFamily: "'Inter', sans-serif",
                          }}>
                            {label}
                          </span>
                        </motion.div>
                      </Link>
                    </motion.div>
                  );
                })}

                <div style={{
                  marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.saffronL}`,
                  textAlign: "center", fontSize: 11, color: C.inkLight,
                  fontFamily: "'Merriweather', serif", fontStyle: "italic", opacity: 0.65,
                }}>
                  ॐ सर्वे भवन्तु सुखिनः
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>
    </>
  );
}